import React, { useRef, useState, useEffect } from 'react';
import { symbols } from './SymbolSvgs';

export const EngravingPreview = ({
  previewImage,
  plateZone,
  text = '',
  selectedFont = 'Arimo',
  fontSize = 40,
  selectedSymbol = 'none',
  symbolPosition = 'before',
}) => {
  const containerRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageWidth, setImageWidth] = useState(0);

  // Font family mapping
  const getFontFamily = (fontName) => {
    const fontMap = {
      'Bodoni': "'Bodoni Moda', serif",
      'Medula': "'Medula One', cursive",
      'Grand Hotel': "'Grand Hotel', cursive",
      'Nickainley': "'Nickainley', cursive",
      'Arimo': "'Arimo', sans-serif",
      'Satisfy': "'Satisfy', cursive",
    };
    return fontMap[fontName] || "'Arimo', sans-serif";
  };

  // Update image width on load and resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const img = containerRef.current.querySelector('img');
        if (img) {
          setImageWidth(img.offsetWidth);
        }
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [imageLoaded]);

  // Scale font size based on image width (base: 800px)
  const scaleFactor = imageWidth / 800;
  const displayFontSize = Math.round(fontSize * scaleFactor);
  const symbolSize = Math.round(displayFontSize * 0.85);

  // Get symbol component
  const SymbolComponent = symbols.find(s => s.id === selectedSymbol)?.component;

  // Split text into lines (max 2)
  const lines = text ? text.split('\n').filter(l => l.length > 0) : [];
  const hasText = lines.length > 0;
  const displayLines = hasText ? lines : ['Prénom'];
  const isPlaceholder = !hasText;

  // Render a single line of text with optional symbol
  const renderLine = (lineText, index) => {
    const showSymbol = index === 0;
    const textOpacity = isPlaceholder ? 0.4 : 1;

    return (
      <div
        key={index}
        className="flex items-center justify-center"
        style={{
          opacity: textOpacity,
          gap: `${displayFontSize * 0.15}px`,
        }}
      >
        {showSymbol && symbolPosition === 'before' && SymbolComponent && (
          <SymbolComponent size={symbolSize} className="flex-shrink-0" />
        )}
        <span className="whitespace-nowrap">{lineText}</span>
        {showSymbol && symbolPosition === 'after' && SymbolComponent && (
          <SymbolComponent size={symbolSize} className="flex-shrink-0" />
        )}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full rounded-xl overflow-hidden"
      data-testid="engraving-preview-container"
    >
      {/* Product Image - Full view without cropping */}
      <img
        src={previewImage || "https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?q=80&w=2071&auto=format&fit=crop"}
        alt="Prévisualisation du produit"
        className="w-full h-auto"
        onLoad={() => setImageLoaded(true)}
        data-testid="preview-image"
      />

      {/* Engraving Zone - Positioned and rotated to match the plate */}
      {plateZone && imageLoaded && (
        <div
          className="absolute flex flex-col items-center justify-center pointer-events-none"
          style={{
            top: `${plateZone.top}%`,
            left: `${plateZone.left}%`,
            width: `${plateZone.width}%`,
            height: `${plateZone.height}%`,
            transform: `translate(-50%, -50%) rotate(${plateZone.angle || 0}deg)`,
            transformOrigin: 'center center',
          }}
          data-testid="engraving-plate-zone"
        >
          {/* Engraving Text */}
          <div
            className="text-center w-full h-full flex flex-col items-center justify-center engraving-text"
            style={{
              fontFamily: getFontFamily(selectedFont),
              fontSize: `${displayFontSize}px`,
              lineHeight: 1.15,
              color: '#1a1a1a',
              textShadow: '0 0 1px rgba(0,0,0,0.1)',
            }}
            data-testid="engraving-text-display"
          >
            {displayLines.map((line, index) => renderLine(line, index))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EngravingPreview;
